import { useState } from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { Check, Circle, Clock } from "lucide-react";

import { AlertNote } from "../../components/AlertNote";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { DataTable } from "../../components/DataTable";
import { DeviceShare } from "../../components/DeviceShare";
import { FilterSwitchGroup } from "../../components/FilterSwitchGroup";
import { MarkdownEditor } from "../../components/MarkdownEditor";
import { MetricCard } from "../../components/MetricCard";
import { Progress } from "../../components/Progress";
import { TrendDelta } from "../../components/TrendDelta";
import { brand } from "../../theme/theme";
import { useTheme as useCustomTheme } from "../../theme/useTheme";
import { markdownToHtml } from "../../utils/markdownClipboard";
import { formatDecimal, formatFraction, formatMinutes, formatNumber } from "../../utils/numberFormat";
import { SCORE_CARDS, STRATEGIES } from "../Clients/metrics/webPerformance.js";
import { SEARCH_QUERY_COLUMNS } from "../Clients/metrics/searchQueries";
import { PlatformIcon } from "../ContentGenerator/workflow/platformMeta";
import { PROJECT_STATE_BADGES, TASK_STATE_BADGES } from "../Projects/stateBadges";
import { KPI_GRID_SX, SURFACE_SX, formatComparedPeriod, hasData, projectsWithActivity, sumTasks } from "./reportFormat";

// ─── Shared pieces ───

const kpiShape = PropTypes.shape({ change_percent: PropTypes.number, change_points: PropTypes.number });

const PercentDelta = ({ kpi }) => (
  <TrendDelta value={kpi.change_percent} suffix=" %" maximumFractionDigits={1} size="small" showEmpty />
);

PercentDelta.propTypes = { kpi: kpiShape.isRequired };

const PointsDelta = ({ kpi, suffix = " pts" }) => (
  <TrendDelta value={kpi.change_points} suffix={suffix} maximumFractionDigits={2} size="small" showEmpty />
);

PointsDelta.propTypes = { kpi: kpiShape.isRequired, suffix: PropTypes.string };

const CardTitleRow = ({ title, meta }) => (
  <Stack direction="row" alignItems="baseline" spacing={1}>
    <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>{title}</Typography>
    {meta && (
      <Typography variant="caption" color="text.secondary" sx={{ ml: "auto !important" }}>
        {meta}
      </Typography>
    )}
  </Stack>
);

CardTitleRow.propTypes = { title: PropTypes.string.isRequired, meta: PropTypes.node };

function SectionHeader({ number, title, meta, tutorialId }) {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      spacing={1.25}
      data-tutorial={tutorialId}
      sx={{ pb: 1.25, borderBottom: `1px solid ${theme.palette.divider}`, scrollMarginTop: "96px" }}
    >
      <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" }}>{number}</Typography>
      <Typography component="h2" sx={{ fontSize: "1.125rem", fontWeight: 600, lineHeight: 1.4 }}>
        {title}
      </Typography>
      {meta && <Box sx={{ ml: "auto", color: "text.secondary", fontSize: "0.75rem" }}>{meta}</Box>}
    </Stack>
  );
}

SectionHeader.propTypes = {
  number: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  meta: PropTypes.node,
  tutorialId: PropTypes.string,
};

/**
 * Headline figure. With `kpi` it shows `format(kpi.value)`, its movement (percent,
 * or points when `points`) and the previous period's value; without it, just `value`.
 */
function KpiCard({ label, kpi, format, points = false, pointsSuffix, hidePrevious = false, value }) {
  return (
    <MetricCard
      label={label}
      value={kpi ? format(kpi.value) : value}
      contentSx={{ gap: 0.5 }}
      footer={
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          {kpi && (points ? <PointsDelta kpi={kpi} suffix={pointsSuffix} /> : <PercentDelta kpi={kpi} />)}
          {kpi && !hidePrevious && (
            <Typography variant="caption" color="text.secondary">
              · {format(kpi.previous)} en el periodo anterior
            </Typography>
          )}
        </Stack>
      }
    />
  );
}

KpiCard.propTypes = {
  label: PropTypes.string.isRequired,
  kpi: PropTypes.object,
  format: PropTypes.func,
  points: PropTypes.bool,
  pointsSuffix: PropTypes.string,
  // SE Ranking KPIs carry no previous value.
  hidePrevious: PropTypes.bool,
  value: PropTypes.string,
};

/** Label and figure over a soft bar. */
const BarRow = ({ label, value, percent }) => (
  <Stack spacing={0.4}>
    <Stack direction="row" sx={{ fontSize: "0.75rem" }}>
      <Box sx={{ flex: 1, color: "text.secondary" }}>{label}</Box>
      <Box component="b" sx={{ fontWeight: 600 }}>
        {value}
      </Box>
    </Stack>
    <Progress soft height={6} value={percent} />
  </Stack>
);

BarRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  percent: PropTypes.number.isRequired,
};

const AiParagraph = ({ text, sx }) => (
  <Typography
    component="div"
    sx={{
      fontSize: "0.875rem",
      lineHeight: 1.6,
      color: "text.primary",
      textWrap: "pretty",
      "& p, & ul, & ol": { m: 0 },
      "& ul, & ol": { pl: 2.5 },
      "& strong": { fontWeight: 600 },
      "& a": { color: "primary.main" },
      ...sx,
    }}
    dangerouslySetInnerHTML={{ __html: markdownToHtml(text) }}
  />
);

AiParagraph.propTypes = { text: PropTypes.string.isRequired, sx: PropTypes.object };

/**
 * AI text that internal users can rewrite while the report is in review.
 * `children({ editButton, body })` lays them out; no `onSave` means read only.
 */
function EditableText({ value, onSave, textSx, children }) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const cancel = () => {
    setDraft(null);
    setError(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      setDraft(null);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo guardar el texto.");
    } finally {
      setSaving(false);
    }
  };

  const editButton =
    onSave && draft === null ? (
      <Button variant="ghost" size="xs" onClick={() => setDraft(value)} sx={{ flexShrink: 0 }}>
        Editar
      </Button>
    ) : null;

  const body =
    draft === null ? (
      <AiParagraph text={value} sx={textSx} />
    ) : (
      <Stack spacing={1}>
        <MarkdownEditor value={draft} onChange={setDraft} minHeight="120px" />
        {error && <AlertNote>{error}</AlertNote>}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="outline" size="sm" onClick={cancel} disabled={saving}>
            Cancelar
          </Button>
          <Button size="sm" onClick={save} disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </Stack>
      </Stack>
    );

  return children({ editButton, body });
}

EditableText.propTypes = {
  value: PropTypes.string.isRequired,
  onSave: PropTypes.func,
  textSx: PropTypes.object,
  children: PropTypes.func.isRequired,
};

/** Written reading opening a data section; nothing when there is no text. */
function AiReading({ text, onSave }) {
  if (!text) return null;
  return (
    <EditableText value={text} onSave={onSave}>
      {({ editButton, body }) => (
        <Card sx={{ flexDirection: "row", alignItems: "flex-start", gap: 1.75, p: "14px 18px" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>{body}</Box>
          {editButton}
        </Card>
      )}
    </EditableText>
  );
}

AiReading.propTypes = {
  text: PropTypes.string,
  onSave: PropTypes.func,
};

// ─── Traffic, visibility and performance ───

const NO_WRAP_SX = { flexWrap: "nowrap" };

const sectionPropTypes = {
  number: PropTypes.string.isRequired,
  analysis: PropTypes.string.isRequired,
  onSaveAnalysis: PropTypes.func,
};

// -- 01 Tráfico y conversiones -------------------------------------------

const CHART_METRICS = [
  { value: "sessions", label: "Sesiones" },
  { value: "total_users", label: "Usuarios" },
  { value: "key_events", label: "Conversiones" },
];

function ChartTooltipContent({ active, payload }) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <Card sx={{ p: 1.25, gap: 0.25, boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)" }}>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {format(parseISO(point.date), "EEEE d 'de' MMMM", { locale: es })}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Periodo actual: <strong>{formatNumber(point.current)}</strong>
      </Typography>
      {point.previous !== undefined && (
        <Typography variant="caption" color="text.secondary">
          Periodo anterior: <strong>{formatNumber(point.previous)}</strong>
        </Typography>
      )}
    </Card>
  );
}

ChartTooltipContent.propTypes = { active: PropTypes.bool, payload: PropTypes.array };

function LegendLine({ color, label }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ width: 14, borderTop: `2px solid ${color}` }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

LegendLine.propTypes = { color: PropTypes.string.isRequired, label: PropTypes.string.isRequired };

function DailyChart({ timeseries }) {
  const theme = useTheme();
  const { neutrals } = useCustomTheme();
  const [metric, setMetric] = useState("sessions");
  const primary = theme.palette.primary.main;

  const data = timeseries.current.map((point, index) => ({
    date: point.date,
    current: point[metric],
    // The previous period can be a day shorter (e.g. 31 vs 30 days).
    previous: timeseries.previous[index]?.[metric],
  }));

  return (
    <Card sx={{ ...SURFACE_SX, gap: 1.75, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={2}>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>Evolución diaria</Typography>
        <Stack direction="row" spacing={1.75}>
          <LegendLine color={primary} label="Periodo actual" />
          <LegendLine color={brand.orange} label="Periodo anterior" />
        </Stack>
        <Box sx={{ ml: "auto", overflowX: "auto" }}>
          <FilterSwitchGroup
            options={CHART_METRICS}
            selectedValue={metric}
            onChange={(value) => value && setMetric(value)}
            containerSx={NO_WRAP_SX}
            optionsSx={NO_WRAP_SX}
          />
        </Box>
      </Stack>
      {/* Grows to the height of the channels and devices column; 220px is the floor. */}
      <Box sx={{ flex: 1, minHeight: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={neutrals.gray[100]} vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={(date) => format(parseISO(date), "d MMM", { locale: es })}
              tick={{ fontSize: 11, fill: neutrals.gray[500] }}
              tickLine={false}
              axisLine={{ stroke: neutrals.gray[200] }}
              minTickGap={32}
            />
            <YAxis
              width={40}
              tick={{ fontSize: 11, fill: neutrals.gray[500] }}
              tickLine={false}
              axisLine={false}
              tickFormatter={formatNumber}
            />
            <ChartTooltip content={<ChartTooltipContent />} cursor={{ stroke: neutrals.gray[300], strokeDasharray: "3 3" }} />
            <Area
              type="monotone"
              dataKey="current"
              stroke={primary}
              strokeWidth={2}
              fill={alpha(primary, 0.08)}
              dot={false}
              activeDot={{ r: 3 }}
            />
            <Line
              type="monotone"
              dataKey="previous"
              stroke={brand.orange}
              strokeWidth={1.5}
              dot={false}
              activeDot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Card>
  );
}

DailyChart.propTypes = { timeseries: PropTypes.object.isRequired };

function ChannelsCard({ channels }) {
  return (
    <Card sx={{ p: "14px 16px", gap: 1 }}>
      <CardTitleRow title="Canales" meta="sesiones" />
      {channels.map((channel) => (
        <BarRow key={channel.channel} label={channel.channel} value={formatNumber(channel.sessions)} percent={channel.share} />
      ))}
    </Card>
  );
}

ChannelsCard.propTypes = { channels: PropTypes.arrayOf(PropTypes.object).isRequired };

function DevicesCard({ devices }) {
  return (
    <Card sx={{ p: "14px 16px", gap: 1.25 }}>
      <CardTitleRow title="Dispositivos" />
      <DeviceShare devices={devices} />
    </Card>
  );
}

DevicesCard.propTypes = { devices: PropTypes.arrayOf(PropTypes.object).isRequired };

function KeyEventsCard({ keyEvents, total }) {
  const max = Math.max(...keyEvents.map((event) => event.count), 1);
  return (
    <Card sx={SURFACE_SX}>
      <CardTitleRow title="Conversiones por objetivo" meta={`${formatNumber(total)} en total`} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
          columnGap: 5,
          rowGap: 1.5,
        }}
      >
        {keyEvents.map((event) => (
          <Stack key={event.event_name} spacing={0.75} sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ fontSize: "0.875rem" }}>
              <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {event.label}
              </Box>
              <Box sx={{ fontWeight: 600 }}>{formatNumber(event.count)}</Box>
              <Box sx={{ width: 64, display: "flex", justifyContent: "flex-end" }}>
                <PercentDelta kpi={event} />
              </Box>
            </Stack>
            <Progress soft height={6} value={(event.count / max) * 100} />
          </Stack>
        ))}
      </Box>
    </Card>
  );
}

KeyEventsCard.propTypes = {
  keyEvents: PropTypes.arrayOf(PropTypes.object).isRequired,
  total: PropTypes.number.isRequired,
};

export function TrafficSection({ data, number, analysis, onSaveAnalysis }) {
  const { kpis } = data;
  // Reports generated before these blocks were collected lack them.
  const channels = data.channels || [];
  const devices = data.devices || [];
  const hasSideColumn = channels.length > 0 || devices.length > 0;
  const hasChart = Boolean(data.timeseries?.current?.length);

  return (
    <Stack spacing={1.75}>
      <SectionHeader
        number={number}
        title="Tráfico y conversiones"
        meta={formatComparedPeriod(data.period)}
        tutorialId="report-traffic-anchor"
      />
      <AiReading text={analysis} onSave={onSaveAnalysis} />
      <Box sx={KPI_GRID_SX}>
        <KpiCard label="Usuarios" kpi={kpis.users} format={formatNumber} />
        <KpiCard label="Sesiones" kpi={kpis.sessions} format={formatNumber} />
        <KpiCard label="Conversiones" kpi={kpis.key_events} format={formatNumber} />
        <KpiCard label="Tasa de conversión" kpi={kpis.conversion_rate} format={formatFraction} points />
      </Box>
      {(hasChart || hasSideColumn) && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              lg: hasChart && hasSideColumn ? "minmax(0, 1fr) 300px" : "minmax(0, 1fr)",
            },
            gap: 1.5,
            alignItems: "stretch",
          }}
        >
          {hasChart && <DailyChart timeseries={data.timeseries} />}
          {hasSideColumn && (
            <Stack spacing={1.5}>
              {channels.length > 0 && <ChannelsCard channels={channels} />}
              {devices.length > 0 && <DevicesCard devices={devices} />}
            </Stack>
          )}
        </Box>
      )}
      {data.key_events.length > 0 && <KeyEventsCard keyEvents={data.key_events} total={kpis.key_events.value} />}
    </Stack>
  );
}

TrafficSection.propTypes = { data: PropTypes.object.isRequired, ...sectionPropTypes };

// -- 02 Visibilidad en Google ---------------------------------------------

function TopPagesCard({ pages, metric, unit }) {
  const max = Math.max(...pages.map((page) => page[metric]), 1);
  return (
    <Card sx={{ p: "14px 16px", gap: 1.25, minWidth: 0 }}>
      <CardTitleRow title="Páginas más visitadas" meta={unit} />
      {pages.map((page) => (
        <Stack key={page.path} spacing={0.4}>
          <Stack direction="row" spacing={1} sx={{ fontSize: "0.75rem" }}>
            <Box sx={{ flex: 1, minWidth: 0, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {page.path}
            </Box>
            <Box sx={{ fontWeight: 600 }}>{formatNumber(page[metric])}</Box>
          </Stack>
          <Progress soft height={6} value={(page[metric] / max) * 100} />
        </Stack>
      ))}
    </Card>
  );
}

TopPagesCard.propTypes = {
  pages: PropTypes.arrayOf(PropTypes.object).isRequired,
  metric: PropTypes.string.isRequired,
  unit: PropTypes.string.isRequired,
};

const PAGES_LIMIT = 5;

export function VisibilitySection({ seo, analytics, number, analysis, onSaveAnalysis }) {
  const searchConsole = seo.search_console;
  const seRanking = seo.seranking;
  const scReady = hasData(searchConsole);
  const srReady = hasData(seRanking);

  const queries = scReady ? searchConsole.queries || [] : [];
  const gaPages = hasData(analytics) ? (analytics.pages || []).filter((page) => !page.is_not_set) : [];
  const pagesCard =
    gaPages.length > 0
      ? { pages: gaPages.slice(0, PAGES_LIMIT), metric: "sessions", unit: "sesiones" }
      : scReady && searchConsole.pages?.length
        ? { pages: searchConsole.pages.slice(0, PAGES_LIMIT), metric: "clicks", unit: "clics" }
        : null;

  const sc = searchConsole.kpis;
  const sr = seRanking.kpis;

  return (
    <Stack spacing={1.75}>
      <SectionHeader number={number} title="Visibilidad en Google" tutorialId="report-visibility-anchor" />
      <AiReading text={analysis} onSave={onSaveAnalysis} />
      {scReady && (
        <Box sx={KPI_GRID_SX}>
          <KpiCard label="Clics" kpi={sc.clicks} format={formatNumber} />
          <KpiCard label="Impresiones" kpi={sc.impressions} format={formatNumber} />
          <KpiCard label="CTR" kpi={sc.ctr} format={formatFraction} points />
          <KpiCard label="Posición media en Google" kpi={sc.position} format={formatDecimal} points pointsSuffix=" puestos" />
        </Box>
      )}
      {srReady && (
        <Box sx={KPI_GRID_SX}>
          {sr.visibility_percent.value !== null && (
            <KpiCard
              label="Visibilidad de tus palabras clave"
              kpi={sr.visibility_percent}
              format={(value) => `${formatDecimal(value, 2)} %`}
              points
              hidePrevious
            />
          )}
          {sr.avg_position.value !== null && (
            <KpiCard
              label="Posición media de tus palabras clave"
              kpi={sr.avg_position}
              format={formatDecimal}
              points
              pointsSuffix=" puestos"
              hidePrevious
            />
          )}
          {sr.keywords_tracked.value > 0 && (
            <>
              <KpiCard label="Palabras clave en top 10" value={`${sr.keywords_top10.value} de ${sr.keywords_tracked.value}`} />
              <KpiCard label="Mejoran / empeoran" value={`${sr.keywords_improved.value} / ${sr.keywords_declined.value}`} />
            </>
          )}
        </Box>
      )}
      {(queries.length > 0 || pagesCard) && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              lg: queries.length > 0 && pagesCard ? "minmax(0, 3fr) minmax(0, 2fr)" : "minmax(0, 1fr)",
            },
            gap: 1.5,
            alignItems: "start",
          }}
        >
          {queries.length > 0 && (
            <DataTable columns={SEARCH_QUERY_COLUMNS} rows={queries} getRowId={(row) => row.query} size="sm" tableMinWidth={480} />
          )}
          {pagesCard && <TopPagesCard {...pagesCard} />}
        </Box>
      )}
    </Stack>
  );
}

VisibilitySection.propTypes = {
  seo: PropTypes.object.isRequired,
  analytics: PropTypes.object.isRequired,
  ...sectionPropTypes,
};

// -- 03 Rendimiento web ------------------------------------------------------

const RING_RADIUS = 26;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

// Lighthouse bands: 90+ good, 50-89 needs work, below 50 poor.
const scoreTone = (theme, score) =>
  score >= 90 ? theme.palette.success.main : score >= 50 ? theme.palette.warning.main : theme.palette.error.main;

function ScoreRing({ label, kpi }) {
  const theme = useTheme();
  const { neutrals } = useCustomTheme();
  const hasScore = kpi.value !== null;
  const color = hasScore ? scoreTone(theme, kpi.value) : neutrals.gray[300];

  return (
    <Stack alignItems="center" spacing={0.75} sx={{ textAlign: "center", minWidth: 0 }}>
      <Box sx={{ position: "relative", width: 64, height: 64 }}>
        <svg viewBox="0 0 60 60" width="64" height="64" style={{ transform: "rotate(-90deg)" }}>
          <circle cx="30" cy="30" r={RING_RADIUS} fill="none" stroke={neutrals.gray[100]} strokeWidth="5" />
          {hasScore && (
            <circle
              cx="30"
              cy="30"
              r={RING_RADIUS}
              fill="none"
              stroke={color}
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={`${(kpi.value / 100) * RING_LENGTH} ${RING_LENGTH}`}
            />
          )}
        </svg>
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.125rem", fontWeight: 600 }}>
          {hasScore ? kpi.value : "—"}
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
        {label}
      </Typography>
      <TrendDelta value={kpi.change_points} suffix=" pts" size="small" />
    </Stack>
  );
}

ScoreRing.propTypes = { label: PropTypes.string.isRequired, kpi: PropTypes.object.isRequired };

export function PerformanceSection({ data, number, analysis, onSaveAnalysis }) {
  const available = STRATEGIES.filter((option) => data.strategies[option.value]);
  const [device, setDevice] = useState(available[0].value);
  const strategy = data.strategies[device];

  return (
    <Stack spacing={1.75}>
      <SectionHeader
        number={number}
        title="Rendimiento web"
        tutorialId="report-performance-anchor"
        meta={
          available.length > 1 ? (
            <FilterSwitchGroup
              options={available}
              selectedValue={device}
              onChange={(value) => value && setDevice(value)}
              containerSx={NO_WRAP_SX}
              optionsSx={NO_WRAP_SX}
            />
          ) : null
        }
      />
      <AiReading text={analysis} onSave={onSaveAnalysis} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: 1.5,
        }}
      >
        <Card sx={{ p: 2.5, display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1 }}>
          {SCORE_CARDS.map(({ key, label }) => (
            <ScoreRing key={key} label={label} kpi={strategy.kpis[key]} />
          ))}
        </Card>
      </Box>
    </Stack>
  );
}

PerformanceSection.propTypes = { data: PropTypes.object.isRequired, ...sectionPropTypes };

// ─── Summary, projects, content and period details ───

const pluralize = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

// -- Resumen ejecutivo + destacados -------------------------------------------

/** One highlight per section the report shows, taken from that section's own data. */
function buildHighlights(report, numbers) {
  const highlights = [];
  const { analytics_data: analytics, seo_data: seo, pagespeed_data: pagespeed } = report;

  if (numbers.traffic && !analytics.error) {
    highlights.push({
      number: numbers.traffic,
      label: "Tráfico",
      value: `${formatNumber(analytics.kpis.key_events.value)} conversiones`,
      detail: <PercentDelta kpi={analytics.kpis.key_events} />,
    });
  }

  if (numbers.visibility) {
    const { search_console: sc, seranking: sr } = seo;
    if (sc.connected && !sc.error) {
      highlights.push({
        number: numbers.visibility,
        label: "Visibilidad",
        value: `${formatNumber(sc.kpis.clicks.value)} clics orgánicos`,
        detail: <PercentDelta kpi={sc.kpis.clicks} />,
      });
    } else if (sr.connected && !sr.error && sr.kpis.visibility_percent.value !== null) {
      highlights.push({
        number: numbers.visibility,
        label: "Visibilidad",
        value: `${formatDecimal(sr.kpis.visibility_percent.value, 2)} % de visibilidad`,
        detail: <PointsDelta kpi={sr.kpis.visibility_percent} />,
      });
    }
  }

  if (numbers.performance) {
    const device = pagespeed.strategies.mobile ? "mobile" : "desktop";
    const score = pagespeed.strategies[device].kpis.performance;
    if (score.value !== null) {
      highlights.push({
        number: numbers.performance,
        label: "Rendimiento",
        value: `${score.value} en ${device === "mobile" ? "móvil" : "ordenador"}`,
        detail: <PointsDelta kpi={score} />,
      });
    }
  }

  if (numbers.projects && report.projects_data.projects.length > 0) {
    const { projects, completed_projects: completed } = report.projects_data;
    const tasksDone = projects.reduce((total, project) => total + sumTasks(project, "tasks_completed"), 0);
    highlights.push({
      number: numbers.projects,
      label: "Proyectos",
      value: pluralize(tasksDone, "tarea completada", "tareas completadas"),
      detail: completed > 0 ? pluralize(completed, "proyecto cerrado", "proyectos cerrados") : null,
    });
  }

  const content = report.content_data;
  if (numbers.content) {
    highlights.push({
      number: numbers.content,
      label: "Contenidos",
      value: pluralize(content.total_approved, "pieza aprobada", "piezas aprobadas"),
      detail: content.campaign_pieces > 0 ? `${content.campaign_pieces} de campañas` : null,
    });
  }

  return highlights;
}

export function ExecutiveSummary({ report, numbers, onSave }) {
  const theme = useTheme();
  const { neutrals } = useCustomTheme();
  const highlights = buildHighlights(report, numbers);
  // The AI leaves the summary empty when there is nothing positive to tell; reviewers can still write one.
  const showSummary = Boolean(report.executive_summary) || Boolean(onSave);
  if (!showSummary && highlights.length === 0) return null;

  return (
    <Card sx={{ gap: 0, overflow: "hidden" }} data-tutorial="report-summary-anchor">
      {showSummary && (
        <EditableText value={report.executive_summary} onSave={onSave} textSx={{ maxWidth: 1000 }}>
          {({ editButton, body }) => (
            <Stack spacing={1.25} sx={{ p: "20px 24px", borderBottom: highlights.length > 0 ? `1px solid ${theme.palette.divider}` : "none" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, flex: 1 }}>Resumen ejecutivo</Typography>
                {editButton}
              </Stack>
              {body}
            </Stack>
          )}
        </EditableText>
      )}
      {highlights.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              md: `repeat(${highlights.length}, minmax(0, 1fr))`,
            },
            backgroundColor: neutrals.gray[50],
          }}
        >
          {highlights.map((highlight) => (
            <Stack
              key={highlight.label}
              spacing={0.25}
              sx={{ p: "12px 16px", borderRight: `1px solid ${theme.palette.divider}`, "&:last-of-type": { borderRight: "none" } }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {highlight.number} · {highlight.label}
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>{highlight.value}</Typography>
              {typeof highlight.detail === "string" ? (
                <Typography variant="caption" color="text.secondary">
                  {highlight.detail}
                </Typography>
              ) : (
                highlight.detail
              )}
            </Stack>
          ))}
        </Box>
      )}
    </Card>
  );
}

ExecutiveSummary.propTypes = {
  report: PropTypes.object.isRequired,
  numbers: PropTypes.object.isRequired,
  onSave: PropTypes.func,
};

// -- Proyectos y tareas -------------------------------------------------------

const TASK_ICONS = { finalizado: Check, en_desarrollo: Clock };

function StateBadge({ state }) {
  return (
    <Badge color={state.color}>
      {state.label}
    </Badge>
  );
}

StateBadge.propTypes = { state: PropTypes.object.isRequired };

const PROJECT_ROW_COLUMNS = {
  xs: "18px minmax(0, 1fr) auto",
  md: "18px minmax(0, 1fr) 110px minmax(120px, 240px) 48px 96px",
};

const DESKTOP_ONLY = { display: { xs: "none", md: "block" } };

function ProjectRow({ project, displayName }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const tasksDone = sumTasks(project, "tasks_completed");
  const tasksTotal = sumTasks(project, "tasks_count");
  const progress = project.is_recurrent ? null : Math.round(project.progress_percentage);

  return (
    <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: PROJECT_ROW_COLUMNS,
          gap: 1.75,
          alignItems: "center",
          p: "12px 16px",
          border: 0,
          background: "none",
          font: "inherit",
          color: "inherit",
          textAlign: "left",
          cursor: "pointer",
          "&:hover": { backgroundColor: theme.palette.action.hover },
        }}
      >
        <Box sx={{ color: "text.secondary", fontWeight: 500, textAlign: "center" }}>{open ? "−" : "+"}</Box>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, minWidth: 0 }}>{displayName}</Typography>
        <Box sx={DESKTOP_ONLY}>
          <StateBadge state={PROJECT_STATE_BADGES[project.status]} />
        </Box>
        <Box sx={DESKTOP_ONLY}>
          {progress === null ? (
            <Typography variant="caption" color="text.secondary">
              Recurrente
            </Typography>
          ) : (
            <Progress height={6} value={progress} />
          )}
        </Box>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, textAlign: "right" }}>
          {progress === null ? "" : `${progress}%`}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ...DESKTOP_ONLY, textAlign: "right" }}>
          {tasksDone} de {tasksTotal} tareas
        </Typography>
      </Box>
      {open && (
        <Stack spacing={1.5} sx={{ p: { xs: "2px 16px 16px", md: "2px 16px 16px 48px" } }}>
          {project.phases.map((phase) => (
            <Stack key={phase.id} spacing={0.25}>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ pb: 0.5 }}>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>{phase.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {phase.tasks_completed} de {phase.tasks_count}
                </Typography>
              </Stack>
              {phase.tasks.map((task) => {
                const state = TASK_STATE_BADGES[task.status] || TASK_STATE_BADGES.planeado;
                const Icon = TASK_ICONS[task.status] || Circle;
                return (
                  <Stack
                    key={task.id}
                    direction="row"
                    alignItems="center"
                    spacing={1.25}
                    sx={{ py: 0.6, borderTop: `1px solid ${theme.palette.divider}`, fontSize: "0.875rem" }}
                  >
                    <Icon size={13} color={theme.palette.text.secondary} style={{ flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>{task.name}</Box>
                    <StateBadge state={state} />
                  </Stack>
                );
              })}
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}

ProjectRow.propTypes = {
  project: PropTypes.object.isRequired,
  displayName: PropTypes.string.isRequired,
};

export function ProjectsSection({ report, number, isClient }) {
  const projectsData = report.projects_data;
  const projects = projectsWithActivity(projectsData);

  // Clients know the onboarding "setup" project as their "itinerario".
  const displayName = (name) => (isClient ? name.replace(/setup/i, "Itinerario") : name);

  return (
    <Stack spacing={1.75}>
      <SectionHeader
        number={number}
        title="Proyectos y tareas"
        tutorialId="report-projects-anchor"
        meta={`${pluralize(projectsData.total_projects, "creado", "creados")} · ${projectsData.active_projects} en progreso · ${pluralize(projectsData.completed_projects, "completado", "completados")}`}
      />
      <Card sx={{ gap: 0, overflow: "hidden" }}>
        {projects.map((project) => (
          <ProjectRow key={project.id} project={project} displayName={displayName(project.name)} />
        ))}
      </Card>
    </Stack>
  );
}

ProjectsSection.propTypes = {
  report: PropTypes.object.isRequired,
  number: PropTypes.string.isRequired,
  isClient: PropTypes.bool,
};

// -- Contenidos -----------------------------------------------------------------

const formatApprovedAt = (isoDate) => format(parseISO(isoDate), "d MMM yyyy", { locale: es });

function ContentPieceRow({ piece }) {
  const theme = useTheme();
  // Reports generated before platforms were collected lack them.
  const platforms = piece.platforms || [];
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ p: "10px 16px", borderBottom: `1px solid ${theme.palette.divider}`, fontSize: "0.875rem", "&:last-of-type": { borderBottom: "none" } }}
    >
      <Box sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}>{piece.title}</Box>
      {platforms.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ display: { xs: "none", md: "flex" } }}>
          {platforms.map((platform) => (
            <PlatformIcon key={platform} platform={platform} size={22} />
          ))}
        </Stack>
      )}
      <Box sx={{ width: 96, textAlign: "right", color: "text.secondary", fontSize: "0.75rem", display: { xs: "none", md: "block" } }}>
        {formatApprovedAt(piece.approved_at)}
      </Box>
    </Stack>
  );
}

ContentPieceRow.propTypes = { piece: PropTypes.object.isRequired };

export function ContentSection({ content, number }) {
  const summary = [
    pluralize(content.total_approved, "pieza aprobada", "piezas aprobadas"),
    ...(content.campaign_pieces > 0 ? [`${content.campaign_pieces} de campañas`] : []),
    ...(content.individual_pieces > 0 ? [pluralize(content.individual_pieces, "individual", "individuales")] : []),
  ];

  return (
    <Stack spacing={1.75}>
      <SectionHeader number={number} title="Contenidos" tutorialId="report-content-anchor" meta={summary.join(" · ")} />
      <Card sx={{ gap: 0, overflow: "hidden" }}>
        {content.pieces.map((piece) => (
          <ContentPieceRow key={piece.id} piece={piece} />
        ))}
      </Card>
    </Stack>
  );
}

ContentSection.propTypes = {
  content: PropTypes.object.isRequired,
  number: PropTypes.string.isRequired,
};

// -- Detalles del periodo -------------------------------------------------------

export function PeriodDetailsSection({ report, number, onSave }) {
  return (
    <Stack spacing={1.75}>
      <SectionHeader number={number} title="Detalles del periodo" tutorialId="report-details-anchor" />
      <EditableText value={report.period_details} onSave={onSave} textSx={{ "& p + p": { mt: 1.25 } }}>
        {({ editButton, body }) => (
          <Card sx={{ ...SURFACE_SX, flexDirection: "row", alignItems: "flex-start", gap: 1.75 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{body}</Box>
            {editButton}
          </Card>
        )}
      </EditableText>
    </Stack>
  );
}

PeriodDetailsSection.propTypes = {
  report: PropTypes.object.isRequired,
  number: PropTypes.string.isRequired,
  onSave: PropTypes.func,
};

// -- Información interna ----------------------------------------------------------

export function InternalSection({ conversation, tickets }) {
  const theme = useTheme();
  const { neutrals } = useCustomTheme();
  const messageShare = (count) => (conversation.total_messages ? (count / conversation.total_messages) * 100 : 0);

  return (
    <Stack
      spacing={1.75}
      data-tutorial="report-internal-anchor"
      sx={{ border: `1px dashed ${neutrals.gray[300]}`, borderRadius: "12px", p: "18px 20px", scrollMarginTop: "96px" }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>Información interna</Typography>
        <Badge color="muted" dot={false}>
          Solo visible para consultores y administradores
        </Badge>
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "320px minmax(0, 1fr)" }, gap: 1.5, alignItems: "start" }}>
        <Card sx={{ p: "14px 16px", gap: 1.25 }}>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 600 }}>{conversation.total_messages}</Typography>
            <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>mensajes intercambiados</Typography>
          </Stack>
          <BarRow label="Equipo" value={conversation.messages_by_team} percent={messageShare(conversation.messages_by_team)} />
          <BarRow label="Cliente" value={conversation.messages_by_client} percent={messageShare(conversation.messages_by_client)} />
          <Stack direction="row" spacing={2.5} sx={{ borderTop: `1px solid ${theme.palette.divider}`, pt: 1.25 }}>
            <Stack>
              <Typography variant="caption" color="text.secondary">
                Tickets
              </Typography>
              <Typography sx={{ fontSize: "1.125rem", fontWeight: 600 }}>{tickets.total_tickets}</Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">
                Tiempo total
              </Typography>
              <Typography sx={{ fontSize: "1.125rem", fontWeight: 600 }}>{formatMinutes(tickets.total_time_consumed_minutes)}</Typography>
            </Stack>
          </Stack>
        </Card>
        {tickets.tickets.length > 0 && (
          <Card sx={{ gap: 0, overflow: "hidden" }}>
            <Box sx={{ p: "10px 16px", borderBottom: `1px solid ${theme.palette.divider}` }}>
              <CardTitleRow title="Tickets del periodo" />
            </Box>
            {tickets.tickets.map((ticket) => {
              const closed = ticket.status === "closed";
              return (
                <Stack
                  key={ticket.id}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ p: "8px 16px", borderBottom: `1px solid ${theme.palette.divider}`, fontSize: "0.875rem", "&:last-of-type": { borderBottom: "none" } }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>{ticket.title}</Box>
                  <Badge color={closed ? "done" : "pending"}>
                    {closed ? "Resuelto" : "Abierto"}
                  </Badge>
                  <Box sx={{ width: 56, textAlign: "right", color: "text.secondary" }}>
                    {ticket.time_consumed_minutes ? formatMinutes(ticket.time_consumed_minutes) : "—"}
                  </Box>
                </Stack>
              );
            })}
          </Card>
        )}
      </Box>
    </Stack>
  );
}

InternalSection.propTypes = {
  conversation: PropTypes.object.isRequired,
  tickets: PropTypes.object.isRequired,
};
